import * as React from 'react'
import Media from 'react-media'
import { useLazyQuery } from '@apollo/react-hooks'
import { LoadScript } from '@react-google-maps/api'
import { GetServerSidePropsContext } from 'next'
import getConfig from 'next/config'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { vsprintf } from 'sprintf-js'

import { BreadcrumbsRenderer } from '@thg-commerce/enterprise-components'
import {
  EnterpriseNextPage,
  i18n,
  PageWrapper,
  Routes,
  useSiteConfig,
  useSiteDefinition,
  useTheme,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import { PageType } from '@thg-commerce/enterprise-core/src/App/types'
import { PageCacheConfiguration } from '@thg-commerce/enterprise-core/src/cache/configuration'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { FullHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import { EnterpriseRequest } from '@thg-commerce/enterprise-core/src/Server/types'
import { Resolvers } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers'
import {
  NearbyStoresArguments,
  NearbyStoresData,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/NearbyStores'
import { isStoreOpen } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/StoreByStoreUrlTag'
import { processesLowerCase } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/Stores'
import {
  mq,
  spacing,
  styled,
  Text,
  ThemeProvider,
} from '@thg-commerce/enterprise-theme'
import { Button } from '@thg-commerce/gravity-elements'
import { StoreMap } from '@thg-commerce/gravity-patterns'
import {
  MapLocationProps,
  MarkerProps,
} from '@thg-commerce/gravity-patterns/StoreMap/MapTypes'
import { StoreSearchResults } from '@thg-commerce/gravity-patterns/StoreSearch/StoreSearchResults'
import { Grid, GridItem } from '@thg-commerce/gravity-system'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

import { NearbyStores as NEARBY_STORES_QUERY } from '../../src/graphql/NearbyStores.graphql'
import { StoreSearchRenderer } from '../../src/renderer/StoreSearchRenderer'
import CustomError from '../_error'

const MapIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/MapIcon'),
)
const ListIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/ListIcon'),
)

const StyledGrid = styled(Grid)`
  padding: 0;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    padding: ${spacing(0)} ${spacing(4)};
  }
`
const SearchResultsLabel = styled.div<{ viewListOnMobile: boolean }>`
  ${Text('small', 'alternate')}
  ${(props) =>
    props.viewListOnMobile
      ? `margin: ${spacing(2)} 0 ;`
      : `margin: ${spacing(2)} 0 0;`}
    
  padding: ${spacing(0)} ${spacing(2)};
  text-align: center;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    text-align: left;
    padding: 0;
    margin: ${spacing(2)} 0;
  }
  span {
    ${Text('small', 'default')}
  }
`

const StyledGridItem = styled(GridItem)`
  height: fit-content;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    height: 1160px;
    overflow: hidden;
  }
`
const ViewSelectorButton = styled(Button)`
  width: calc(100% - ${spacing(4)});
  margin: ${spacing(2)} ${spacing(2)} 0;

  button {
    display: flex;
    align-items: center;
    gap: 11px;

    :hover,
    :active,
    :focus,
    :focus-visible,
    :focus-within {
      svg > path,
      svg > g > rect,
      svg > g > path {
        fill: ${(props) => props.theme.colors.palette.greys.white};
      }
    }
  }
`

const MapWrapper = styled.div`
  width: 100vw;
  max-width: 100%;
  height: 655px;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    height: 1160px;
    overflow: hidden;
  }
`

const prepareMapData = (props: NearbyStoresData) => {
  const data: MarkerProps[] = []
  if (Array.isArray(props)) {
    props.forEach((item, index) => {
      data.push({
        position: {
          lat: item.latitude,
          lng: item.longitude,
        },
        address: item.address,
        title: item.displayName,
        markerId: Number(index + 1),
        store: item,
      })
    })
  }
  return data
}

const LONDON_CENTER = {
  lat: 51.509865,
  lng: -0.118092,
}

const StoreLocator: EnterpriseNextPage<{ location: string }> = (props: {
  location: string
}) => {
  const { enableStoreLocator, geocodeApiKeyStoreMap } = useSiteConfig()
  const { brand, originUrl } = useSiteDefinition()
  const [markers, setMarkers] = React.useState<MarkerProps[]>([])
  const [viewListOnMobile, setViewListOnMobile] = React.useState<boolean>(true)
  const [resultsFor, setResultsFor] = React.useState<string>('')
  const [mapCenter, setMapCenter] = React.useState<MapLocationProps>(
    LONDON_CENTER,
  )
  const [mapReady, setMapReady] = React.useState<boolean>()
  const [selectedNearbyStore, setSelectedNearbyStore] = React.useState<number>(
    0,
  )
  const theme = useTheme()

  const [scriptReady, setScriptReady] = React.useState<boolean>(false)
  if (!enableStoreLocator) {
    return <CustomError statusCode={404} />
  }

  const storeSearchTitle = (brand: string) =>
    i18n('storelocator.storesearch.searchpage.title', brand)

  const i18nText = {
    pageTitle: i18n('storelocator.storesearch.page.title'),
    getDirectionsButton: i18n('storelocator.storedetails.directionsbutton'),
    storeDetailsButton: i18n('storelocator.storedetails.detailsbutton'),
    milesText: i18n('storelocator.storedetails.distanceunits'),
    storePermClosed: i18n('storelocator.storedetails.permanentlyclosed'),
    storeTempClosed: i18n('storelocator.storedetails.temporarilyclosed'),
    storeClosedText: i18n('storelocator.storedetails.closed'),
    storeOpenText: i18n('storelocator.storedetails.open'),
    storeSearchCTA: i18n('storelocator.storesearch.cta'),
    storeSearchResultsText: i18n('storelocator.storesearch.results.text'),
    showMoreText: i18n('general.list.show.more'),
    breadCrumbsHome: i18n('general.breadcrumbs.home.text'),
    breadCrumbsStoreFinder: i18n('storelocator.breadcrumbs.storefinder'),
    storeSearch: {
      title: storeSearchTitle(brand.charAt(0).toUpperCase() + brand.slice(1)),
      subtitle: i18n('storelocator.storesearch.subtitle'),
      inputLabel: i18n('storelocator.storesearch.inputlabel'),
      buttonLabel: i18n('storelocator.storesearch.buttonlabel'),
      infoLabel: i18n('storelocator.storesearch.infolabel'),
      infoLinkLabel: i18n('storelocator.storesearch.infolinklabel'),
      viewMap: i18n('storelocator.storesearch.searchpage.viewmap'),
      viewList: i18n('storelocator.storesearch.searchpage.viewlist'),
    },
    storeDetailsCard: {
      storeOpenText: i18n('storelocator.storedetails.open'),
      storeClosedText: i18n('storelocator.storedetails.closed'),
      storeTempClosed: i18n('storelocator.storedetails.temporarilyclosed'),
      storePermClosed: i18n('storelocator.storedetails.permanentlyclosed'),
      storeDetailsButton: i18n('storelocator.storedetails.detailsbutton'),
      getDirectionsButton: i18n('storelocator.storedetails.directionsbutton'),
      milesText: i18n('storelocator.storedetails.distanceunits'),
    },
  }

  const [getNearbyStores, { data: nearbyStoresData }] = useLazyQuery<
    { nearbyStores: NearbyStoresData },
    NearbyStoresArguments
  >(NEARBY_STORES_QUERY, { fetchPolicy: 'no-cache' })

  React.useEffect(() => {
    const filteredMapData = prepareMapData(nearbyStoresData?.nearbyStores || [])
    setMarkers(filteredMapData)
    setMapCenter(filteredMapData[0]?.position || LONDON_CENTER)
    setSelectedNearbyStore(-1)
  }, [nearbyStoresData])

  const storeDetailsHref = (town: string, storeUrlTag: string) => {
    return Routes.StoreDetails.replace(
      ':town',
      processesLowerCase(town || ''),
    ).replace(':store', storeUrlTag)
  }
  const sessionStorage = typeof window !== 'undefined' && window.sessionStorage

  const breadcrumbItems = [
    {
      text: i18nText.breadCrumbsHome,
      url: Routes.HomePage,
    },
    {
      text: i18nText.breadCrumbsStoreFinder,
      url: Routes.StoreLocator,
    },
  ]

  return (
    <React.Fragment>
      <Head>
        <title>{i18nText.pageTitle}</title>
        <link
          rel="canonical"
          href={`${originUrl}${Routes.StoreLocator}`}
          key="link_canonical"
        />
      </Head>
      <PageWrapper compactMargin={true} sendPageVisit={true}>
        <Media query={mq(theme.breakpointUtils.map, 'md', true)}>
          {(isDesktop) => (
            <GridItem
              style={{ paddingBottom: isDesktop ? '80px' : '40px' }}
              colSpan={12}
            >
              <ThemeProvider theme={theme}>
                <BreadcrumbsRenderer
                  breadcrumbLabel={i18nText.breadCrumbsStoreFinder}
                  schemaItems={breadcrumbItems.map((breadcrumb, index) => {
                    return {
                      '@type': 'ListItem',
                      position: index + 1,
                      name: breadcrumb.text,
                      item: originUrl.concat(breadcrumb.url),
                    }
                  })}
                  breadcrumbs={breadcrumbItems}
                  capitalizeText
                />
                <StyledGrid>
                  <StyledGridItem
                    colStart={1}
                    colSpan={[12, 12, 5, 5]}
                    rowStart={2}
                  >
                    <StoreSearchRenderer
                      i18nText={i18nText.storeSearch}
                      alignment={HorizontalAlignment.LEFT}
                      isSearchReady={isDesktop ? mapReady : scriptReady}
                      getNearbyStores={getNearbyStores}
                      setResultsFor={setResultsFor}
                      locationParam={props.location}
                      showInfoLabel={true}
                    />
                    {!isDesktop && nearbyStoresData && (
                      <ViewSelectorButton
                        emphasis="medium"
                        onClick={() => setViewListOnMobile(!viewListOnMobile)}
                      >
                        {viewListOnMobile ? <MapIcon /> : <ListIcon />}
                        {viewListOnMobile
                          ? i18nText.storeSearch.viewMap
                          : i18nText.storeSearch.viewList}
                      </ViewSelectorButton>
                    )}
                    {nearbyStoresData && (
                      <SearchResultsLabel viewListOnMobile={viewListOnMobile}>
                        {vsprintf(i18nText.storeSearchResultsText, [
                          nearbyStoresData.nearbyStores.length.toString(),
                        ])}
                        <span>{` ${resultsFor}`}</span>
                      </SearchResultsLabel>
                    )}
                    {(isDesktop || viewListOnMobile) && nearbyStoresData && (
                      <StoreSearchResults
                        selectedNearbyStore={selectedNearbyStore}
                        setSelectedNearbyStore={setSelectedNearbyStore}
                        i18nText={{
                          getDirectionsButton: i18nText.getDirectionsButton,
                          storeDetailsButton: i18nText.storeDetailsButton,
                          milesText: i18nText.milesText,
                          storePermClosed: i18nText.storePermClosed,
                          storeTempClosed: i18nText.storeTempClosed,
                          storeClosedText: i18nText.storeClosedText,
                          storeOpenText: i18nText.storeOpenText,
                          showMoreText: i18nText.showMoreText,
                        }}
                        stores={nearbyStoresData?.nearbyStores.map((store) => {
                          return {
                            ...store,
                            status: isStoreOpen(store.openingTimes),
                          }
                        })}
                        storeDetailsOnClick={() => {
                          if (sessionStorage) {
                            sessionStorage.setItem(
                              'storeDetailsPrevPath',
                              'searchPage',
                            )
                          }
                        }}
                        storeDetailsHref={storeDetailsHref}
                      />
                    )}
                  </StyledGridItem>
                  <LoadScript
                    id="google-map"
                    googleMapsApiKey={geocodeApiKeyStoreMap || ''}
                    libraries={['places']}
                    loadingElement={<React.Fragment />}
                    onLoad={() => setScriptReady(true)}
                  />
                  {scriptReady && (isDesktop || !viewListOnMobile) && (
                    <GridItem
                      colStart={[1, 1, 6, 6]}
                      colSpan={[12, 12, 7, 7]}
                      rowStart={[3, 3, 2, 2]}
                    >
                      <MapWrapper>
                        <StoreMap
                          {...theme?.patterns?.storeSearch?.storeMap}
                          markers={markers}
                          cardText={i18nText.storeDetailsCard}
                          center={mapCenter}
                          apiKey={geocodeApiKeyStoreMap}
                          selectedMarkerId={selectedNearbyStore}
                          pinClickCallback={(value) =>
                            setSelectedNearbyStore(value || 0)
                          }
                          selectedPinColor={{
                            fillColor: theme.colors.palette.accent.base,
                            textColor: theme.colors.palette.greys.white,
                          }}
                          hoverPinColor={{
                            fillColor: theme.colors.palette.accent.base,
                            textColor: theme.colors.palette.greys.white,
                          }}
                          mapReadyCallback={setMapReady}
                          storeDetailsHref={storeDetailsHref}
                          scriptLoaded={scriptReady}
                        />
                      </MapWrapper>
                    </GridItem>
                  )}
                </StyledGrid>
              </ThemeProvider>
            </GridItem>
          )}
        </Media>
      </PageWrapper>
    </React.Fragment>
  )
}

export default StoreLocator

StoreLocator.Layout = FullHeaderLayout
StoreLocator.pageType = PageType.STORE_LOCATOR

export const getServerSideProps = withCacheConfiguration<
  { props: { location: string } },
  GetServerSidePropsContext
>(
  async (
    context: GetServerSidePropsContext,
  ): Promise<{
    props: { location: string }
    cache?: PageCacheConfiguration
  }> => {
    const nextConfig = getConfig() as NextConfig
    const request = context.req as EnterpriseRequest
    const config = await loadConfiguration(nextConfig, request)
    const { publicRuntimeConfig } = config
    const apollo = createApolloWithRequest(request, config, true)
    const allowOverrides =
      request.headers['x-enterprise-allow-overrides'] === '1'
    const allowSitePropertyOverrides =
      request.headers['x-enterprise-allow-site-property-overrides'] === '1'

    apollo.addResolvers(
      Resolvers(
        apollo,
        publicRuntimeConfig,
        allowOverrides,
        allowSitePropertyOverrides,
        request.config.enableVary,
      ),
    )

    const { location } = request.query

    return {
      props: { location: decodeURIComponent(location?.toString() || '') },
    }
  },
)
